import type { PortfolioProjectKey } from '@/lib/portfolioProjects';

type L = { tr: string; en: string };

export type CaseStudyObservedMetric = {
  value: L;
  label: L;
};

/** Client-owned figures published with explicit source + period; never presented as growth or Figtures-only impact. */
export type CaseStudyObservation = {
  heading: L;
  metrics: CaseStudyObservedMetric[];
  source: L;
  caveat: L;
};

export type CaseStudyDetail = {
  project: PortfolioProjectKey;
  client: L;
  problem: L;
  role: L;
  scope: L[];
  timeline: Array<{ label: L; value: L }>;
  outcome: L;
  observation?: CaseStudyObservation;
  measurementPlan?: L;
  /** Heading + note for the four-tile grid so client capability figures are not read as Figtures results. */
  highlights: { heading: L; note: L };
  serviceSlugs: string[];
};

/**
 * Approved case content (client approval 2026-10-30). Only facts already on the site are used:
 * no invented metrics, dates or quotes. Timeline dates are portfolio publication / measurement windows.
 */
export const CASE_STUDY_DETAILS: Partial<Record<PortfolioProjectKey, CaseStudyDetail>> = {
  p1: {
    project: 'p1',
    client: {
      tr: '3D Yanında — 3D tarama, CAD modelleme, tersine mühendislik ve hassas baskı yapan dijital üretim atölyesi (Türkiye).',
      en: '3D Yanında — a digital manufacturing workshop offering 3D scanning, CAD modelling, reverse engineering and precision printing (Turkey).',
    },
    problem: {
      tr: 'Atölyenin teknik yetkinliği ve hızı dijital vitrinde anlaşılmıyordu. Bulunamayan bir yedek parçayı arayan bakım sorumlusu veya sanayici, sitede tedarikçiye güvenmesini sağlayacak sinyalleri ve teklif istemenin net bir yolunu bulamıyordu.',
      en: 'The workshop’s technical capability and speed did not come across online. A maintenance lead or manufacturer searching for a part that no longer exists could not find the trust signals or a clear way to request a quote.',
    },
    role: {
      tr: 'Figtures, kurumsal web sitesinin bilgi mimarisi, UX/UI tasarımı ve geliştirmesini tek ekip olarak üstlendi. Süreç haftalık kısa durum görüşmeleriyle yürütüldü. Atölyenin üretim hizmetleri ve kapasite bilgileri 3D Yanında’ya aittir.',
      en: 'Figtures owned the corporate website’s information architecture, UX/UI design and development as one team, run with short weekly status calls. The production services and capacity figures belong to 3D Yanında.',
    },
    scope: [
      {
        tr: 'SCAN → CAD → PRINT akışını merkeze alan kurumsal site ve hizmet anlatımı',
        en: 'Corporate site and service narrative built around the SCAN → CAD → PRINT flow',
      },
      {
        tr: 'Oto yedek parça ve endüstriyel kullanım senaryolarını anlatan içerik yapısı',
        en: 'Content structure for automotive spares and industrial replacement use cases',
      },
      {
        tr: 'Teklif taleplerini tek akışta toplayan, ölçülebilir CTA’lar',
        en: 'Measurable CTAs that gather quote requests into a single flow',
      },
      {
        tr: 'Next.js tabanlı performanslı vitrin, CMS destekli içerik alanları ve hizmet sayfaları arasında iç link ağı',
        en: 'Performance-focused Next.js front end, CMS-ready content zones and internal links between service pages',
      },
    ],
    timeline: [
      { label: { tr: 'Portföyde yayın', en: 'Added to portfolio' }, value: { tr: 'Mayıs 2026', en: 'May 2026' } },
      {
        label: { tr: 'Teslim sonrası gözlem dönemi', en: 'Post-delivery observation window' },
        value: { tr: '23 Temmuz – 21 Ekim 2026', en: '23 July – 21 October 2026' },
      },
    ],
    outcome: {
      tr: 'Site 3dyaninda.com adresinde canlıda. Teslimden sonraki üç aylık dönemde site, Google web aramasında ortalama ilk sayfa konumunda görünüyor ve organik tıklama alıyor. Bu sonuç; müşterinin hizmetleri, içerikleri ve marka bilinirliğiyle birlikte oluşur, yalnızca Figtures’ın katkısı değildir.',
      en: 'The site is live at 3dyaninda.com. In the three months after delivery it appears, on average, on the first page of Google web search and receives organic clicks. This outcome reflects the client’s services, content and brand awareness as well; it is not attributable to Figtures alone.',
    },
    observation: {
      heading: { tr: 'Teslim sonrası gözlenen arama verisi', en: 'Search data observed after delivery' },
      metrics: [
        { value: { tr: '386', en: '386' }, label: { tr: 'Toplam tıklama', en: 'Total clicks' } },
        { value: { tr: '19,6 bin', en: '19.6K' }, label: { tr: 'Toplam gösterim', en: 'Total impressions' } },
        { value: { tr: '~%2', en: '~2%' }, label: { tr: 'Ortalama TO', en: 'Average CTR' } },
        { value: { tr: '~5,4', en: '~5.4' }, label: { tr: 'Ortalama konum', en: 'Average position' } },
      ],
      source: {
        tr: 'Kaynak: müşterinin Google Search Console verisi (https://3dyaninda.com/, Performans, web araması), son 3 ay, 23 Tem–21 Eki 2026.',
        en: 'Source: the client’s Google Search Console data (https://3dyaninda.com/, Performance, web search), last 3 months, 23 Jul–21 Oct 2026.',
      },
      caveat: {
        tr: 'Önceki döneme ait karşılaştırma verisi olmadığı için büyüme veya yüzde artış iddia edilmez; rakamlar teslim sonrası gözlenen durumu gösterir.',
        en: 'There is no baseline for an earlier period, so no growth or percentage increase is claimed; the figures describe the observed state after delivery.',
      },
    },
    highlights: {
      heading: { tr: 'Sitede anlatılan müşteri kapasitesi', en: 'Client capability presented on the site' },
      note: {
        tr: 'Aşağıdaki değerler 3D Yanında’nın kendi üretim kapasitesi ve geçmiş iş bilgisidir; Figtures’ın sağladığı sonuç değildir.',
        en: 'The values below are 3D Yanında’s own production capacity and track record, not results delivered by Figtures.',
      },
    },
    serviceSlugs: ['web-development', 'ui-ux-design'],
  },
  p3: {
    project: 'p3',
    client: {
      tr: 'Outfica — global pazara yönelik, yapay zeka destekli moda SaaS ürünü.',
      en: 'Outfica — an AI-powered fashion SaaS product for a global market.',
    },
    problem: {
      tr: 'Outfit Generator, Flat Lay ve Dress Up gibi birden fazla AI aracının, katalog akışıyla birlikte, teknik olmayan bir kullanıcının da rahatça kullanabileceği tek ve sade bir panelde toplanması gerekiyordu.',
      en: 'Several AI tools — Outfit Generator, Flat Lay, Dress Up and more — had to sit alongside the catalogue flow in one clean dashboard that a non-technical user could navigate with ease.',
    },
    role: {
      tr: 'Figtures, SaaS panelinin ürün ve arayüz tasarımını Outfica ekibiyle birlikte yürüttü: model çıktılarının kullanıcıya nasıl gösterileceği, araçlar arası gezinme ve uzun oturumlara uygun karanlık tema. Moda AI modelleri ve iş modeli Outfica’ya aittir.',
      en: 'Figtures led the SaaS dashboard’s product and interface design together with the Outfica team: how model outputs are presented, navigation between tools, and a dark theme suited to long sessions. The fashion AI models and business model belong to Outfica.',
    },
    scope: [
      {
        tr: 'AI araçlarını (6+ modül) ve katalog akışını tek çatıda toplayan panel yapısı',
        en: 'Dashboard structure bringing 6+ AI tool modules and the catalogue flow under one roof',
      },
      {
        tr: 'Kredi, kuyruk ve kampanya alanlarıyla operasyon odaklı düzen',
        en: 'Operations-first layout with credits, queue and campaign areas',
      },
      {
        tr: 'Gerçek zamanlı katalog önizleme ve model çıktılarının sunumu',
        en: 'Live catalogue previews and presentation of model outputs',
      },
      {
        tr: 'Uzun oturumlar için karanlık tema ve çok dile genişletilebilir arayüz iskeleti',
        en: 'Dark theme for long sessions and an interface shell ready for more languages',
      },
    ],
    timeline: [
      { label: { tr: 'Portföyde yayın', en: 'Added to portfolio' }, value: { tr: 'Mayıs 2026', en: 'May 2026' } },
      { label: { tr: 'Durum', en: 'Status' }, value: { tr: 'outfica.com canlıda', en: 'Live at outfica.com' } },
    ],
    outcome: {
      tr: 'AI araçları ve katalog deneyimi tek panelde birleşti; ürün outfica.com adresinde canlıda. Kullanım ve dönüşüm metrikleri müşteriye aittir ve henüz yayın onayı alınmadığı için burada paylaşılmıyor.',
      en: 'The AI tools and catalogue experience now live in one dashboard, and the product is live at outfica.com. Usage and conversion metrics belong to the client and are not published here because they have not been approved for release.',
    },
    measurementPlan: {
      tr: 'Ölçüm planı: araç başına kullanım, ilk başarılı çıktıya kadar geçen süre ve kredi tüketimi. Veriler müşteri onayıyla yayınlanacak.',
      en: 'Measurement plan: usage per tool, time to first successful output, and credit consumption. Figures will be published once the client approves them.',
    },
    highlights: {
      heading: { tr: 'Teslim edilen ürün özellikleri', en: 'Delivered product features' },
      note: {
        tr: 'Aşağıdakiler ölçülmüş iş sonuçları değil, teslim edilen panelin özellikleridir.',
        en: 'These are features of the delivered dashboard, not measured business results.',
      },
    },
    serviceSlugs: ['ui-ux-design', 'saas-mvp-development'],
  },
  p10: {
    project: 'p10',
    client: {
      tr: 'Backdoor Tickets — Almanya’daki mekanlar ve promoter’lar için etkinlik biletleme SaaS ürünü.',
      en: 'Backdoor Tickets — an event ticketing SaaS product for venues and promoters in Germany.',
    },
    problem: {
      tr: 'Gece ekonomisinde biletleme; promoter satış takibini, kapıdaki en yoğun anda hızlı girişi ve Almanya pazarında pazarlık konusu olmayan veri egemenliğini aynı üründe karşılamak zorundaydı.',
      en: 'Nightlife ticketing had to cover promoter sales tracking, fast entry at the busiest moment at the door, and data sovereignty — non-negotiable in the German market — in one product.',
    },
    role: {
      tr: 'Figtures, ürün arayüzü ve SaaS landing deneyiminin tasarım ve geliştirmesini tek ekip olarak üstlendi. İş, işe ekranlardan önce operasyonu anlamakla başladı ve İstanbul’daki ekiple tamamen uzaktan yürütüldü.',
      en: 'Figtures designed and built the product interface and SaaS landing experience as one team. The work started with operations rather than screens and was run fully remotely from Istanbul.',
    },
    scope: [
      {
        tr: 'Promoter operasyon takibi için ürün arayüzü',
        en: 'Product interface for promoter operations tracking',
      },
      {
        tr: 'Yoğun giriş (crunch-time) senaryolarına göre kapı görevlisi ekranları',
        en: 'Door-staff views designed for crunch-time entry',
      },
      {
        tr: 'Veri kontrolünü öne çıkaran, mekan odaklı SaaS landing sayfası',
        en: 'Venue-focused SaaS landing page that foregrounds data control',
      },
      {
        tr: 'Demo ve erken erişim talep akışı',
        en: 'Demo and early-access request flow',
      },
    ],
    timeline: [
      { label: { tr: 'Portföyde yayın', en: 'Added to portfolio' }, value: { tr: 'Mayıs 2026', en: 'May 2026' } },
      { label: { tr: 'Durum', en: 'Status' }, value: { tr: 'Erken erişim / demo aşaması', en: 'Early access / demo stage' } },
    ],
    outcome: {
      tr: 'Ürün arayüzü ve demo / erken erişim akışı teslim edildi. Ürün erken erişim aşamasında olduğundan yayınlanabilir bir kullanım veya satış metriği henüz yok.',
      en: 'The product interface and the demo / early-access flow were delivered. The product is in early access, so there are no publishable usage or sales metrics yet.',
    },
    measurementPlan: {
      tr: 'Ölçüm planı: demo ve erken erişim talepleri, etkinlik başına kapı giriş süresi ve promoter panel kullanımı. Veriler müşteri onayıyla yayınlanacak.',
      en: 'Measurement plan: demo and early-access requests, door entry time per event, and promoter dashboard usage. Figures will be published once the client approves them.',
    },
    highlights: {
      heading: { tr: 'Ürünün odak alanları', en: 'Product focus areas' },
      note: {
        tr: 'Aşağıdakiler ölçülmüş sonuçlar değil, teslim edilen arayüzün odaklandığı kullanım senaryolarıdır.',
        en: 'These are the use cases the delivered interface focuses on, not measured results.',
      },
    },
    serviceSlugs: ['ui-ux-design', 'saas-mvp-development'],
  },
};

export const CASE_STUDY_SECTION_TITLES = {
  client: { tr: 'Müşteri', en: 'Client' },
  problem: { tr: 'Problem', en: 'Problem' },
  role: { tr: 'Figtures’ın rolü', en: 'Figtures’ role' },
  scope: { tr: 'Teslim kapsamı', en: 'Delivery scope' },
  timeline: { tr: 'Zaman çizelgesi', en: 'Timeline' },
  outcome: { tr: 'Sonuç', en: 'Outcome' },
  discovery: {
    title: { tr: 'Benzer bir ihtiyacınız mı var?', en: 'Facing a similar challenge?' },
    desc: {
      tr: 'Hedeflerinizi ve mevcut durumunuzu paylaşın. Bir iş günü içinde dönüş yapar, ücretsiz bir keşif görüşmesi planlarız; ardından kapsam, takvim ve bütçeyi içeren yazılı teklif gelir.',
      en: 'Share your goals and current setup. We reply within one business day and schedule a free discovery call, followed by a written proposal covering scope, timeline and budget.',
    },
    button: { tr: 'Keşif görüşmesi talep edin', en: 'Request a discovery call' },
  },
} satisfies Record<string, unknown>;

export function getCaseStudyDetail(key: PortfolioProjectKey): CaseStudyDetail | null {
  return CASE_STUDY_DETAILS[key] ?? null;
}

/** Contact link that tags the lead with the case it came from (non-PII ids only). */
export function buildCaseStudyContactHref(projectSlug: string): string {
  const params = new URLSearchParams({ source: 'case_study', utm_content: projectSlug });
  return `/contact?${params.toString()}`;
}
